// Angular
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';

// Angular Material
import { MatIconModule } from '@angular/material/icon';

// Third party
import { Subscription, takeUntil } from 'rxjs';

// Local library
import { MatNotificationService, UnsubscribeObservableService,
TranslatePipe
} from '@amwager/util';
import {
  AccountFacade,
  ProfileService,
  ResponsibleGamingService,
  ResponsibleGamingType
} from '@amwager/account/data-access';
import { errorCodeHandlerResponse, isOnline$ } from '@amwager/util-ts';
import {
  CommonFacade,
  ENVIRONMENT,
  PusherService,
  SessionService
} from '@amwager/data-access';
import { WagerFacade } from '@amwager/wager/data-access';
import { AuthFacade } from '@amwager/authentication/data-access';
import { FundingFacade } from '@amwager/funding/data-access';
import { TicketFacade } from '@amwager/ticket/data-access';

/**
 * Responsible Gaming panel inside the profile — Timeout / Self-Exclude
 * request form (ported from the Ionic profile screen, with the title
 * separated from the content card like Help).
 */
@Component({
  selector: 'feature-mat-responsible-gaming',
  templateUrl: './responsible-gaming.component.html',
  styleUrls: ['./responsible-gaming.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    TranslatePipe
  ]
})
export class ResponsibleGamingComponent extends UnsubscribeObservableService implements OnInit {
  private subsSelfExcludeOrSuspendResponse!: Subscription;
  private subsSelfExcludeOrSuspendVerificationResponse!: Subscription;

  responsibleGamingForm!: FormGroup;
  responsibleType!: string;
  verificationToken!: string;
  isProccesing = false;
  isOnline = true;

  constructor(
    private readonly pusherService: PusherService,
    private readonly authFacade: AuthFacade,
    private readonly ticketFacade: TicketFacade,
    private readonly fundingFacade: FundingFacade,
    private readonly accountFacade: AccountFacade,
    private readonly commonFacade: CommonFacade,
    private readonly wagerFacade: WagerFacade,
    private readonly formBuilder: FormBuilder,
    private readonly matNotificationService: MatNotificationService,
    private readonly sessionService: SessionService,
    private readonly profileService: ProfileService,
    private readonly responsibleGamingService: ResponsibleGamingService,
    private readonly router: Router,
    readonly cdRef: ChangeDetectorRef,
    @Inject(ENVIRONMENT) readonly environment: string
  ) {
    super();
  }

  /** Convenience getter for easy access to form fields. */
  get formVal() {
    return this.responsibleGamingForm?.controls;
  }

  ngOnInit(): void {
    this.checkConnectivity();
    this.initForm();
    this.responsibleType = 'timeout';

    this.formVal['days'].setValidators(
      [Validators.required, Validators.min(3), Validators.max(365)]
    );
    this.formVal['days'].updateValueAndValidity();
  }

  /**
   * Close the panel and return the profile to its default section
   * @returns {void}
   */
  close(): void {
    this.profileService.setActiveSection('default');
  }

  /**
   * Switch between the Timeout and Self-Exclude request types
   * @param {string} type 'timeout' | 'self-exclude'
   * @returns {void}
   */
  responsibleTypeIs(type: string): void {
    this.responsibleType = type;
    this.responsibleGamingForm.reset();

    this.formVal['days'].clearValidators();
    this.formVal['selfExclude'].clearValidators();

    // Re-init so cleared validators don't linger across type switches.
    this.initForm();

    switch (type) {
      case 'timeout':
        this.formVal['days'].setValidators(
          [Validators.required, Validators.min(3), Validators.max(365)]
        );
        this.formVal['days'].updateValueAndValidity();
        break;
      case 'self-exclude':
        this.formVal['selfExclude'].setValidators([Validators.required]);
        this.formVal['selfExclude'].updateValueAndValidity();
        break;
    }
  }

  /**
   * Validate and submit the timeout/self-exclude request
   * @returns {void}
   */
  doresponsibleGamingForm(): void {
    if (!this.isOnline) {
      this.commonFacade.setIsConnectedInternet('ResponsibleGamingComponent:doresponsibleGamingForm', false);
      return;
    }

    if ((this.responsibleGamingForm.invalid || this.responsibleGamingForm.errors) && !this.isProccesing) {
      if (this.responsibleType === 'timeout') this.formVal['days'].markAsTouched();
      else this.formVal['selfExclude'].markAsTouched();
      return;
    }

    this.isProccesing = true;

    const isExclusion: boolean = this.responsibleType === 'self-exclude';
    const { days, selfExclude } = this.responsibleGamingForm.value;

    this.responsibleGamingForm.disable();

    this.accountFacade.selfExcludeOrSuspend(Number(days) || 0, Number(selfExclude) || 0, isExclusion);
    this.subsSelfExcludeOrSuspendResponse = this.accountFacade.isSelfExcludeOrSuspendSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe(({ response, isSuccess }) => {
        this.responsibleGamingForm.enable();
        this.isProccesing = false;

        // Undefined means no result yet — effects drive the action.
        if (isSuccess === undefined) return;

        if (isSuccess) {
          this.verificationToken = response.VerificationToken;
          this.showAlertMessage(this.messageToShow());

          this.subsSelfExcludeOrSuspendResponse?.unsubscribe();
          this.cdRef.markForCheck();
          return;
        }

        this.responsibleGamingForm.setErrors({
          apiResponse: response?.error?.MessageCode
            ? response?.error?.Message || errorCodeHandlerResponse(response?.status)
            : errorCodeHandlerResponse(response?.status)
        });

        this.subsSelfExcludeOrSuspendResponse?.unsubscribe();
        this.cdRef.markForCheck();
      });
  }

  /**
   * Confirm the request via token, then log the user out
   * @param {string} verificationToken Token from the submit response
   * @returns {void}
   */
  doVerification(verificationToken: string): void {
    this.accountFacade.selfExcludeOrSuspendVerification(verificationToken);
    this.subsSelfExcludeOrSuspendVerificationResponse =
      this.accountFacade.isSelfExcludeOrSuspendVerificationSuccess$()
        .pipe(takeUntil(this.toUnsubscribe$))
        .subscribe(({ response, isSuccess }) => {
          // Undefined/success actions are driven by the effects.
          if (isSuccess === undefined) return;

          if (isSuccess) {
            this.responsibleGamingForm.reset();
            this.profileService.closeProfile();

            this.pusherService.unSubscribeCustomer();
            this.sessionService.resetLogout();

            this.commonFacade.setIsAuthenticated(false, 'ResponsibleGamingComponent:doVerification');
            this.wagerFacade.setIsShowBetPreview(false);

            // Keep — re-establishes a guest session after logout.
            this.authFacade.loginGuest();

            this.router.navigate(['/login']);

            this.accountFacade.clearState('ResponsibleGamingComponent:doVerification');
            this.authFacade.clearState('ResponsibleGamingComponent:doVerification');
            this.fundingFacade.clearState('ResponsibleGamingComponent:doVerification');
            this.ticketFacade.clearState('ResponsibleGamingComponent:doVerification');
            this.wagerFacade.clearState('ResponsibleGamingComponent:doVerification');
            this.commonFacade.clearState('ResponsibleGamingComponent:doVerification');

            this.subsSelfExcludeOrSuspendVerificationResponse?.unsubscribe();
            this.cdRef.markForCheck();
            return;
          }

          this.responsibleGamingForm.setErrors({
            apiResponse: response?.error?.MessageCode
              ? response?.error?.Message || errorCodeHandlerResponse(response?.status)
              : errorCodeHandlerResponse(response?.status)
          });

          this.subsSelfExcludeOrSuspendVerificationResponse?.unsubscribe();
          this.cdRef.markForCheck();
        });
  }

  /**
   * Build the confirmation prompt from the selected option/period
   * @returns {string} Confirmation message
   */
  messageToShow(): string {
    return this.responsibleGamingService.buildConfirmationMessage(
      this.responsibleType as ResponsibleGamingType,
      this.formVal['days'].value,
      this.formVal['selfExclude'].value
    );
  }

  /**
   * Show the confirm dialog; on Proceed, run verification
   * @param {string} message Confirmation message
   * @returns {void}
   */
  showAlertMessage(message: string): void {
    this.matNotificationService
      .showAlert('Info', message, '', 'Proceed', 'Cancel')
      .subscribe((confirmed: boolean) => {
        if (confirmed) this.doVerification(this.verificationToken);
      });
  }

  /**
   * Watch online/offline state and sync with the common facade
   * @returns {void}
   */
  checkConnectivity(): void {
    isOnline$(this.environment)
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe((online: boolean) => {
        this.isOnline = online;
        this.commonFacade.setIsConnectedInternet('ResponsibleGamingComponent:checkConnectivity', online);
      });
  }

  private initForm(): void {
    this.responsibleGamingForm = this.formBuilder.group({
      days: [''],
      selfExclude: ['']
    });
  }
}
